import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  useUserStore,
  useGamesStore,
  usePostsStore,
  useHighlightsStore,
} from "../stores/StoreProvider";
import {
  MessageCircle,
  Building,
  Trophy,
  Target,
  TrendingUp,
  Share2,
  Eye,
  Heart,
  MoreVertical,
  Play,
  Edit,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FeedList } from "../components/feed";
import { useFeed } from "../hooks/useFeed";

const Dashboard = observer(() => {
  const user = useUserStore();
  const gamesStore = useGamesStore();
  const postsStore = usePostsStore();
  const highlightsStore = useHighlightsStore();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [postContent, setPostContent] = useState('');

  useEffect(() => {
    if (user.isAuthenticated) {
      gamesStore.fetchGames();
      gamesStore.fetchFollowingGames();
      postsStore.fetchFollowingPosts();
      highlightsStore.fetchFollowingHighlights();
    }
  }, [user.isAuthenticated]);

  const onRefresh = async () => {
    await Promise.all([
      gamesStore.fetchFollowingGames(),
      highlightsStore.fetchFollowingHighlights(),
      postsStore.fetchFollowingPosts(),
    ]);
  };

  // Get current season stats
  const seasonStats = gamesStore.getSeasonStats();

  const handleVideoPress = (highlight) => {
    setSelectedVideo(highlight);
    setShowVideoModal(true);
  };

  const handleEditGame = (game) => {
    // TODO: Implement edit game functionality
    toast.info("Edit game functionality coming soon!");
  };

  const handleDeleteGame = async (gameId) => {
    try {
      await gamesStore.deleteGame(gameId);
      await gamesStore.fetchFollowingGames();
      toast.success("Game deleted successfully");
    } catch (error) {
      toast.error("Failed to delete game");
    }
  };

  const handleEditHighlight = (highlight) => {
    // TODO: Implement edit highlight functionality
    toast.info("Edit highlight functionality coming soon!");
  };

  const handleDeleteHighlight = async (highlightId) => {
    try {
      await highlightsStore.deleteHighlight(highlightId);
      await highlightsStore.fetchFollowingHighlights();
      toast.success("Highlight deleted successfully");
    } catch (error) {
      toast.error("Failed to delete highlight");
    }
  };

  const handleEditPost = (post) => {
    // TODO: Implement edit post functionality
    toast.info("Edit post functionality coming soon!");
  };

  const handleDeletePost = async (postId) => {
    try {
      await postsStore.deletePost(postId);
      await postsStore.fetchFollowingPosts();
      toast.success("Post deleted successfully");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };



  // Use the feed hook to combine and sort feed items
  const { combinedFeed } = useFeed(
    gamesStore.followingGames,
    highlightsStore.followingHighlights,
    postsStore.followingPosts
  );

  // Limit to 3 items for dashboard
  const dashboardFeed = combinedFeed.slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">
          Welcome, {user.profile?.first_name || "User"}!
        </h1>
        <p className="text-blue-100">Ready to improve your game today?</p>
      </div>

      {/* Create Post Section - Facebook Style */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-start space-x-4">
          {/* User Avatar */}
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
            {user.profile?.avatar ? (
              <img 
                src={user.profile.avatar} 
                alt="Avatar" 
                className="w-12 h-12 rounded-full object-cover" 
              />
            ) : (
              <span className="text-white font-semibold text-lg">
                {user.profile?.first_name?.charAt(0) || user.profile?.last_name?.charAt(0) || 'U'}
              </span>
            )}
          </div>
          
          {/* Post Input Area */}
          <div className="flex-1">
            <div className="relative">
              <textarea
                placeholder={`What's on your mind, ${user.profile?.first_name || 'User'}?`}
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (postContent.trim()) {
                      const postData = {
                        content: postContent.trim(),
                        organization_id: user.profile?.organization?.id
                      };
                      postsStore.createPost(postData)
                        .then(() => {
                          setPostContent(''); // Clear the textarea
                          postsStore.fetchFollowingPosts(); // Refresh the feed
                          toast.success('Post created successfully!');
                        })
                        .catch((error) => {
                          console.error('Post creation error:', error);
                          toast.error(error.message || 'Failed to create post');
                        });
                    } else {
                      toast.error('Please add some content to post');
                    }
                  }
                }}
                className="w-full p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 focus:bg-white focus:border-blue-500 transition-colors resize-none"
                rows="3"
                style={{ minHeight: '80px' }}
              />
              <button
                onClick={async () => {
                  if (postContent.trim()) {
                    try {
                      const postData = {
                        content: postContent.trim(),
                        organization_id: user.profile?.organization?.id
                      };
                      await postsStore.createPost(postData);
                      setPostContent(''); // Clear the textarea
                      await postsStore.fetchFollowingPosts(); // Refresh the feed
                      toast.success('Post created successfully!');
                    } catch (error) {
                      console.error('Post creation error:', error);
                      toast.error(error.message || 'Failed to create post');
                    }
                  } else {
                    toast.error('Please add some content to post');
                  }
                }}
                className={`absolute right-3 top-3 flex items-center space-x-2 transition-colors ${
                  postContent.trim() 
                    ? 'text-blue-600 hover:text-blue-700' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Share2 size={20} />
                <span className="text-sm">Post</span>
              </button>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">

              
              <Link
                to="/app/add-game"
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
              >
                <Trophy size={20} />
                <span className="text-sm font-medium">Add Game</span>
              </Link>
              
              <Link
                to="/app/season"
                className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
              >
                <TrendingUp size={20} />
                <span className="text-sm font-medium">Add Highlight</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Chat and Organization Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Chat with Coach Button */}
        <Link
          to="/app/chat"
          className="bg-blue-600 hover:bg-blue-700 text-white p-6 rounded-2xl transition-colors duration-200"
        >
          <div className="flex flex-col items-center justify-center h-full">
            <MessageCircle size={24} className="mb-3" />
            <h3 className="text-lg font-semibold text-center">
              Chat with Coach Biscuit
            </h3>
          </div>
        </Link>

        {/* Organization Card */}
        <Link
          to="/app/organization"
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
        >
          <div className="flex flex-col items-center justify-center h-full">
            {user.profile?.organization?.image_url ? (
              <img
                src={user.profile.organization.image_url}
                alt="Organization"
                className="w-12 h-12 rounded-lg mb-3 object-contain"
              />
            ) : (
              <Building size={24} className="text-blue-600 mb-3" />
            )}
            <div className="text-center">
              <h3 className="font-semibold text-gray-900">
                {user.profile?.organization?.name || "No Organization"}
              </h3>
              {user.profile?.organization?.team_name && (
                <p className="text-sm text-blue-600 font-medium">
                  {user.profile.organization.team_name}
                </p>
              )}
            </div>
          </div>
        </Link>
      </div>

      {/* Current Season Stats */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Current Season Stats
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {seasonStats.wins}
            </div>
            <div className="text-sm text-gray-600">Wins</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-red-600 mb-1">
              {seasonStats.losses}
            </div>
            <div className="text-sm text-gray-600">Losses</div>
          </div>
          {user.profile?.position === 'goalie' ? (
            // Goalie Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {seasonStats.totalSaves}
                </div>
                <div className="text-sm text-gray-600">Saves</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {seasonStats.savePercentage}%
                </div>
                <div className="text-sm text-gray-600">Save %</div>
              </div>
            </>
          ) : (
            // Skater Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {seasonStats.totalGoals}
                </div>
                <div className="text-sm text-gray-600">Goals</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {seasonStats.totalAssists}
                </div>
                <div className="text-sm text-gray-600">Assists</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Friends Activity Feed */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Friends Activity
          </h2>
          <Link
            to="/app/friends"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            View All
          </Link>
        </div>

        <FeedList
          items={dashboardFeed}
          onEdit={(item) => {
            if (item.content_type === "game") handleEditGame(item);
            else if (item.content_type === "highlight")
              handleEditHighlight(item);
            else if (item.content_type === "post") handleEditPost(item);
          }}
          onDelete={(item) => {
            if (item.content_type === "game") handleDeleteGame(item.id);
            else if (item.content_type === "highlight")
              handleDeleteHighlight(item.id);
            else if (item.content_type === "post") handleDeletePost(item.id);
          }}
          onVideoPress={handleVideoPress}
          emptyMessage="No activity from friends yet"
          className="space-y-4"
        />
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{selectedVideo.title}</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">Video player coming soon</p>
            </div>
          </div>
        </div>
      )}


    </div>
  );
});

export default Dashboard;
